import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  LocalShipping as ShippingIcon,
  EmojiEvents as TrophyIcon,
  Inventory as ProductIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import ordersService from '../../services/orders.service';
import shopService, { Store } from '../../services/shop.service';

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#e91e63', '#00bcd4'];
const STATUS_COLORS: Record<string, string> = {
  pendente: '#ff9800',
  confirmado: '#2196f3',
  preparando: '#9c27b0',
  enviado: '#00bcd4',
  entregue: '#4caf50',
  cancelado: '#f44336',
};

const StoreDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState(30);
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [stores, setStores] = useState<Store[]>([]);

  // Dashboard data
  const [salesChart, setSalesChart] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [storeRanking, setStoreRanking] = useState<any[]>([]);
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [storeDashboard, setStoreDashboard] = useState<any>(null);

  const fetchStores = useCallback(async () => {
    try {
      const response = await shopService.getStores({ limit: 100 });
      if (response.success) {
        setStores(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const storeId = selectedStore !== 'all' ? selectedStore : undefined;

      // Fetch all dashboard data in parallel
      const [salesRes, productsRes, rankingRes, hourlyRes] = await Promise.all([
        ordersService.getSalesChart({ storeId, period, groupBy: 'day' }),
        ordersService.getTopProducts({ storeId, period, limit: 10 }),
        ordersService.getStoreRanking({ period, limit: 10 }),
        ordersService.getHourlyDistribution({ storeId, period }),
      ]);

      if (salesRes.success) setSalesChart(salesRes.data);
      if (productsRes.success) setTopProducts(productsRes.data);
      if (rankingRes.success) setStoreRanking(rankingRes.data);
      if (hourlyRes.success) setHourlyData(hourlyRes.data);

      // If a specific store is selected, fetch its detailed dashboard
      if (storeId) {
        const dashboardRes = await ordersService.getStoreDashboard(storeId, period);
        if (dashboardRes.success) {
          setStoreDashboard(dashboardRes.data);
        }
      } else {
        // For all stores, get overall stats
        const statsRes = await ordersService.getOrderStats(undefined, period);
        if (statsRes.success) {
          setStoreDashboard({
            kpis: {
              totalOrders: statsRes.data.total,
              totalRevenue: statsRes.data.totalValue,
              averageOrderValue: statsRes.data.averageValue,
              pendingOrders: statsRes.data.pending,
              deliveredOrders: statsRes.data.delivered,
            },
            ordersByStatus: [
              { status: 'pendente', count: statsRes.data.pending },
              { status: 'confirmado', count: statsRes.data.confirmed },
              { status: 'preparando', count: statsRes.data.preparing },
              { status: 'enviado', count: statsRes.data.shipped },
              { status: 'entregue', count: statsRes.data.delivered },
              { status: 'cancelado', count: statsRes.data.cancelled },
            ].filter(s => s.count > 0),
          });
        }
      }
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
      setError(t('shop.errorLoadingDashboard'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, period]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const formatDate = (dateStr: string | number) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit' });
  };

  // KPI Cards
  const kpiCards = storeDashboard?.kpis ? [
    {
      title: t('shop.totalOrders'),
      value: storeDashboard.kpis.totalOrders,
      icon: <CartIcon />,
      color: '#2196f3',
    },
    {
      title: t('shop.totalRevenue'),
      value: formatCurrency(storeDashboard.kpis.totalRevenue),
      icon: <MoneyIcon />,
      color: '#4caf50',
      isText: true,
    },
    {
      title: t('shop.averageValue'),
      value: formatCurrency(storeDashboard.kpis.averageOrderValue),
      icon: <TrendingUpIcon />,
      color: '#9c27b0',
      isText: true,
    },
    {
      title: t('shop.pendingOrders'),
      value: storeDashboard.kpis.pendingOrders,
      icon: <ScheduleIcon />,
      color: '#ff9800',
    },
    {
      title: t('shop.deliveredOrders'),
      value: storeDashboard.kpis.deliveredOrders,
      icon: <ShippingIcon />,
      color: '#00bcd4',
    },
  ] : [];

  if (loading && !storeDashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('shop.dashboard')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            size="small"
            label={t('shop.storeLabel')}
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">{t('shop.allStores')}</MenuItem>
            {stores.map((store) => (
              <MenuItem key={store.id} value={store.id}>
                {store.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('shop.period')}
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={7}>{t('shop.days7')}</MenuItem>
            <MenuItem value={30}>{t('shop.days30')}</MenuItem>
            <MenuItem value={60}>{t('shop.days60')}</MenuItem>
            <MenuItem value={90}>{t('shop.days90')}</MenuItem>
          </TextField>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: `${card.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography variant="h5" fontWeight={600}>
                  {card.isText ? card.value : card.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Sales Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('shop.salesByDay')}
            </Typography>
            {salesChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={300} debounce={50}>
                <AreaChart data={salesChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tickFormatter={formatDate} />
                  <YAxis yAxisId="left" orientation="left" stroke="#2196f3" />
                  <YAxis yAxisId="right" orientation="right" stroke="#4caf50" />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === 'revenue' ? formatCurrency(Number(value) || 0) : value,
                      name === 'revenue' ? t('shop.revenue') : t('shop.orderLabel'),
                    ]}
                    labelFormatter={(label) => formatDate(label as string)}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="orders"
                    name={t('shop.orderLabel')}
                    stroke="#2196f3"
                    fill="#2196f320"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    name={t('shop.revenue')}
                    stroke="#4caf50"
                    fill="#4caf5020"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('shop.noDataAvailable')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Orders by Status */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('shop.ordersByStatus')}
            </Typography>
            {storeDashboard?.ordersByStatus?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250} debounce={50}>
                <PieChart>
                  <Pie
                    data={storeDashboard.ordersByStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status"
                    label={({ name, value }: any) => `${ordersService.getStatusLabel(name)}: ${value}`}
                  >
                    {storeDashboard.ordersByStatus.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any, name: any) => [value, name ? ordersService.getStatusLabel(name as any) : '']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('shop.noDataAvailable')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Hourly Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimeIcon color="primary" />
              <Typography variant="h6">{t('shop.hourlyDistribution')}</Typography>
            </Box>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250} debounce={50}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} />
                  <YAxis />
                  <Tooltip labelFormatter={(h) => `${h}:00 - ${h}:59`} />
                  <Bar dataKey="orders" name={t('shop.orderLabel')} fill="#2196f3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('shop.noDataAvailable')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Top Products */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ProductIcon color="primary" />
              <Typography variant="h6">{t('shop.topProducts')}</Typography>
            </Box>
            {topProducts.length > 0 ? (
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('shop.productCol')}</TableCell>
                      <TableCell align="right">{t('shop.qty')}</TableCell>
                      <TableCell align="right">{t('shop.revenue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {product.productName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={product.totalQuantity} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(product.totalRevenue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('shop.noDataAvailable')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Store Ranking */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TrophyIcon color="primary" />
              <Typography variant="h6">{t('shop.storeRanking')}</Typography>
            </Box>
            {storeRanking.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>{t('shop.rank')}</TableCell>
                      <TableCell>{t('shop.storeLabel')}</TableCell>
                      <TableCell align="right">{t('shop.orderLabel')}</TableCell>
                      <TableCell align="right">{t('shop.totalRevenue')}</TableCell>
                      <TableCell align="right">{t('shop.averageValue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storeRanking.map((store) => (
                      <TableRow key={store.storeId} hover>
                        <TableCell>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor:
                                store.rank === 1
                                  ? '#ffd700'
                                  : store.rank === 2
                                  ? '#c0c0c0'
                                  : store.rank === 3
                                  ? '#cd7f32'
                                  : '#e0e0e0',
                              color: store.rank <= 3 ? 'white' : 'text.primary',
                              fontWeight: 600,
                            }}
                          >
                            {store.rank}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {store.storeName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={store.totalOrders} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {formatCurrency(store.totalRevenue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(store.avgOrderValue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('shop.noDataAvailable')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreDashboard;
